import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";

import { format, isAfter, isBefore, isValid } from "date-fns";

import {
  Event,
  StopCircle,
  AddLocation,
  FmdBad,
  Info,
} from "@mui/icons-material";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  Chip as MuiChip,
  Divider as MuiDivider,
  LinearProgress as MuiLinearProgress,
  Typography as MuiTypography,
  Pagination as MuiPagination,
  Paper as MuiPaper,
  CircularProgress as MuiCircularProgress,
  Tooltip,
  TextField as MuiTextField,
  Alert as MuiAlert,
  Stepper,
  Step,
  StepLabel,
  Box,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { StepIconProps } from "@mui/material/StepIcon";

import { spacing, SpacingProps, Stack } from "@mui/system";

import { useGetActivitiesForContractQuery } from "../../redux/slices/indexApiSlice";

import { IContract, LifecycleChange } from "../../types/contract";
import { useTranslation } from "react-i18next";
import { IStep } from "../../types/step";
import { subPeriodFromDate } from "../../utils/string";
import { IDocument } from "../../types/document";
import { ContractExtractionResult } from "../../types/aiOutput";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import { enGB, nl } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import OwlAvatarIcon from "../../icons/OwlAvatarIcon";
import useBotChat from "../../hooks/useBotChat";

const Alert = styled(MuiAlert)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const Card = styled(MuiCard)(spacing);

const Chip = styled(MuiChip)(spacing);

const Divider = styled(MuiDivider)(spacing);

const LinearProgress = styled(MuiLinearProgress)(spacing);

const Spacer = styled.div(spacing);

const Pagination = styled(MuiPagination)(spacing);

const Paper = styled(MuiPaper)(spacing);

const CircularProgress = styled(MuiCircularProgress)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  color:
    theme.palette.mode === "dark"
      ? theme.palette.secondary.main
      : theme.palette.primary.main,
  zIndex: 1,
  // color: "#fff",
  width: 40,
  height: 40,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
    // boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
  }),
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <AddLocation />,
    2: <FmdBad />,
    3: <StopCircle />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const DashedTimelineConnector = styled(TimelineConnector)(({ theme }) => ({
  borderStyle: "dashed",
  borderWidth: 2,
  borderColor: theme.palette.divider,
  backgroundColor: "transparent",
  borderLeft: "none",
  borderTop: "none",
  borderBottom: "none",
}));

const TimelineContentTitle = ({
  change,
  isFollowUp,
}: {
  change: LifecycleChange;
  isFollowUp?: boolean;
}) => {
  const { t } = useTranslation();

  const [title, setTitle] = useState<string | null>("");

  useEffect(() => {
    if (!change) {
      return;
    }

    setTitle(
      `${
        change.purpose === "other"
          ? t("New contract event")
          : t(change.purpose!)
      } ${
        change.signatureDate
          ? isFollowUp
            ? t("is active")
            : t("was signed")
          : isFollowUp
          ? t("is active")
          : t("was not signed")
      }`
    );
  }, [change]);

  return <Typography sx={{ fontWeight: 700 }}>{title}</Typography>;
};

interface ITimelineProgressBarProps {
  contract: IContract;
  documents?: IDocument[];
}
const BasicTimeline = ({ contract, documents }: ITimelineProgressBarProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [noticeDate, setNoticeDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { setOpen: setShowChat, setAddedMessage } = useBotChat();

  const today = new Date();

  useEffect(() => {
    if (contract.lifecycle?.changes?.length) {
      const lastChange =
        contract.lifecycle.changes[contract.lifecycle.changes.length - 1];

      if (lastChange.newEndDate && isValid(new Date(lastChange.newEndDate))) {
        setEndDate(new Date(lastChange.newEndDate));
      } else {
        setEndDate(null);
      }
    } else {
      if (
        contract.dates?.endDate &&
        isValid(new Date(contract.dates.endDate))
      ) {
        setEndDate(new Date(contract.dates.endDate));
      } else {
        setEndDate(null);
      }
    }
  }, [contract.lifecycle, contract.dates]);

  useEffect(() => {
    if (contract.noticePeriod && endDate) {
      const notice = subPeriodFromDate(endDate, contract.noticePeriod);
      setNoticeDate(notice);
    } else {
      setNoticeDate(endDate);
    }
  }, [contract.noticePeriod, endDate, i18n.language]);

  const getDocumentName = (documentId: string) => {
    const document = documents?.find((doc) => doc.id === documentId);
    return document ? document.name : documentId;
  };

  const switchToDocument = (id: string) => {
    const doc = documents?.find((doc) => doc.id === id);
    if (doc?.type === "contract") {
      navigate(`/contract/${doc.contractId}`, {
        replace: true,
      });
    } else if (doc?.type === "attachment") {
      // select the attachment
      // setDocument(doc);
      navigate(`/contract/${doc.contractId}/document/${doc.id}`, {
        replace: true,
      });
    }
  };

  const launchChat = (question: string) => {
    setShowChat(true);

    // console.log("Enter key pressed");
    // postQuestion({ question: event.target.value });
    setAddedMessage(question);
  };

  return (
    <Timeline
      // position="right"
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          maxWidth: 130,
        },
      }}
    >
      {contract.lifecycle?.initial?.signatureDate &&
        isValid(new Date(contract.lifecycle?.initial.signatureDate)) && (
          <TimelineItem>
            <TimelineOppositeContent>
              <Typography sx={{ fontWeight: 700 }}>
                {format(
                  new Date(contract.lifecycle.initial.signatureDate),
                  "PP",
                  {
                    locale: i18n.language === "nl" ? nl : enGB,
                  }
                )}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Typography sx={{ fontWeight: 700 }}>
                {t("Signature date")}
              </Typography>

              {contract.lifecycle.initial.summary}

              <Box pb={4}>
                <Link
                  onClick={() =>
                    navigate(`/contract/${contract.id}`, {
                      replace: true,
                    })
                  }
                  sx={{
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "none",
                    },
                  }}
                >
                  {contract.name}
                </Link>
              </Box>
            </TimelineContent>
          </TimelineItem>
        )}

      {contract.lifecycle?.initial?.startDate &&
        isValid(new Date(contract.lifecycle?.initial.startDate)) && (
          <TimelineItem>
            <TimelineOppositeContent>
              <Typography sx={{ fontWeight: 700 }}>
                {format(new Date(contract.lifecycle.initial.startDate), "PP", {
                  locale: i18n.language === "nl" ? nl : enGB,
                })}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Typography sx={{ fontWeight: 700 }}>
                {t("Start date")}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        )}

      {contract.lifecycle?.changes?.map((change, index) => {
        const signatureDate =
          change.signatureDate && isValid(new Date(change.signatureDate))
            ? new Date(change.signatureDate)
            : null;
        const changeDate =
          change.changeDate && isValid(new Date(change.changeDate))
            ? new Date(change.changeDate)
            : null;
        const newStartDate =
          change.newStartDate && isValid(new Date(change.newStartDate))
            ? new Date(change.newStartDate)
            : null;
        const newEndDate =
          change.newEndDate && isValid(new Date(change.newEndDate))
            ? new Date(change.newEndDate)
            : null;

        // signing date or change date is in the past
        const isSigned = signatureDate && isBefore(signatureDate, today);
        const isStarted = newStartDate && isBefore(newStartDate, today);
        const isEnded = newEndDate && isBefore(newEndDate, today);

        const documentName = change.referenceDocumentId
          ? getDocumentName(change.referenceDocumentId)
          : "";

        return (
          <>
            <TimelineItem key={index}>
              <TimelineOppositeContent>
                <Typography sx={{ fontWeight: 700 }}>
                  {signatureDate
                    ? format(signatureDate, "PP", {
                        locale: i18n.language === "nl" ? nl : enGB,
                      })
                    : changeDate
                    ? format(changeDate, "PP", {
                        locale: i18n.language === "nl" ? nl : enGB,
                      })
                    : t("No date")}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  variant={
                    isSigned
                      ? "filled"
                      : isStarted
                      ? "filled"
                      : isEnded
                      ? "filled"
                      : "outlined"
                  }
                />
                {isStarted && isEnded ? (
                  // If the change has a new end date, make the connector dashed
                  <TimelineConnector />
                ) : (
                  <DashedTimelineConnector />
                )}
              </TimelineSeparator>
              <TimelineContent>
                <TimelineContentTitle change={change} />

                <Stack direction="column" gap={2}>
                  <Box>{change.summary}</Box>

                  <Box>
                    {signatureDate && (
                      <Typography>
                        {t("Signature date")}:{" "}
                        {format(new Date(signatureDate), "PP", {
                          locale: i18n.language === "nl" ? nl : enGB,
                        })}
                      </Typography>
                    )}
                    {change.signedBy && change.signedBy.length > 0 && (
                      <Box>
                        <Typography>
                          {t("Signed by")}: {change.signedBy.join(", ")}
                        </Typography>
                      </Box>
                    )}

                    {newStartDate && (
                      <Box>
                        <Typography>
                          {t("New start date")}:{" "}
                          {format(new Date(newStartDate), "PP", {
                            locale: i18n.language === "nl" ? nl : enGB,
                          })}
                        </Typography>
                      </Box>
                    )}
                    {newEndDate && (
                      <Box>
                        <Typography>
                          {t("New end date")}:{" "}
                          {format(new Date(newEndDate), "PP", {
                            locale: i18n.language === "nl" ? nl : enGB,
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {change.referenceDocumentId && (
                    <Box>
                      <Link
                        onClick={() =>
                          switchToDocument(change.referenceDocumentId || "")
                        }
                        sx={{
                          cursor: "pointer",
                          fontWeight: 500,
                          "&:hover": {
                            textDecoration: "none",
                          },
                        }}
                      >
                        {documentName}
                      </Link>
                    </Box>
                  )}

                  <Box pb={4}>
                    <Button
                      variant="outlined"
                      startIcon={<OwlAvatarIcon />}
                      onClick={() =>
                        launchChat(
                          t("contract_timeline.chat_question", {
                            documentName,
                          })
                        )
                      }
                    >
                      {t("Tell me more")}
                    </Button>
                  </Box>
                </Stack>
              </TimelineContent>
            </TimelineItem>

            {/* {change.newStartDate && (
            <TimelineItem>
              <TimelineOppositeContent>
                <Typography sx={{ fontWeight: 700 }}>
                  {format(new Date(change.newStartDate), "PP")}
                </Typography>
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot variant="outlined" />
                {change.newEndDate === endDate &&
                isAfter(new Date(endDate), today) ? (
                  // If the change has a new end date, make the connector dashed
                  <DashedTimelineConnector />
                ) : (
                  <TimelineConnector />
                )}
              </TimelineSeparator>
              <TimelineContent>
                <Typography sx={{ fontWeight: 700 }}>
                  <TimelineContentTitle change={change} isFollowUp />
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )} */}
          </>
        );
      })}

      {noticeDate && isAfter(noticeDate, today) && contract.noticePeriod && (
        <TimelineItem>
          <TimelineOppositeContent>
            <Typography sx={{ fontWeight: 700 }}>
              {format(noticeDate, "PP", {
                locale: i18n.language === "nl" ? nl : enGB,
              })}
            </Typography>
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot variant="filled" />
            <DashedTimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Typography sx={{ fontWeight: 700 }}>
              {t("Latest termination date")}
            </Typography>

            {t("notification_period_date_text", {
              value: contract.noticePeriod?.value ?? 0,
              unit: t(contract.noticePeriod?.unit),
              beforeDate: endDate
                ? t("date.dateOnly", {
                    date: subPeriodFromDate(endDate, contract.noticePeriod),
                  })
                : t("No end date"),
            })}
          </TimelineContent>
        </TimelineItem>
      )}

      {endDate && (
        <TimelineItem>
          <TimelineOppositeContent>
            <Typography sx={{ fontWeight: 700 }}>
              {format(endDate, "PP", {
                locale: i18n.language === "nl" ? nl : enGB,
              })}
            </Typography>
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot
              variant={isBefore(endDate, today) ? "filled" : "outlined"}
            />
          </TimelineSeparator>
          <TimelineContent>
            <Typography sx={{ fontWeight: 700 }}>
              {isBefore(endDate, today) ? t("Contract ended") : t("End date")}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      )}
    </Timeline>
  );
};

interface ITimelineStepperProps {
  contract: IContract;
}
const TimelineStepper = ({ contract }: ITimelineStepperProps) => {
  const { t } = useTranslation();
  const [steps, setSteps] = useState<IStep[] | null>([]);

  useEffect(() => {
    const newSteps = [];

    let latestEndDate: Date | null = contract.dates?.endDate
      ? new Date(contract.dates?.endDate)
      : null;

    if (contract.lifecycle) {
      if (contract.lifecycle.initial?.startDate) {
        const startDate = new Date(contract.lifecycle.initial.startDate);

        const startStep: IStep = {
          icon: <Event />,
          label: format(startDate, "PP"),
          labelProps: {
            optional: t("Start date"),
          },
          description: contract.lifecycle?.initial?.summary || "",
          created: startDate.getTime(),
        };
        newSteps.push(startStep);
      }

      latestEndDate = contract.lifecycle.initial?.endDate
        ? new Date(contract.lifecycle.initial.endDate)
        : null;

      contract.lifecycle.changes?.forEach((change) => {
        if (change.newEndDate) {
          const newEndDate = new Date(change.newEndDate);
          if (!latestEndDate || isAfter(newEndDate, latestEndDate)) {
            latestEndDate = newEndDate;
          }
        }

        if (change.signatureDate && change.changeDate) {
          const newChangeStep: IStep = {
            icon: <Event />,
            label: format(new Date(change.signatureDate), "PP"),
            labelProps: {
              optional: t("Signed"),
            },
            description: (
              <Box>
                <Typography></Typography>{" "}
                {format(new Date(change.signatureDate), "PP")}
                change.summary
              </Box>
            ),
            created: new Date(change.changeDate).getTime(),
          };
          newSteps.push(newChangeStep);
        }
      });
    } else {
      // There is no lifecycle, fall back to the contract dates
      if (contract.dates?.startDate) {
        const startDate = new Date(contract.dates.startDate);
        const startStep: IStep = {
          icon: <Event />,
          label: format(startDate, "PP"),
          labelProps: {
            optional: t("Start date"),
          },
          description: "",
          created: startDate.getTime(),
        };
        newSteps.push(startStep);
      }

      if (contract.dates?.signatureDate) {
        const signatureDate = new Date(contract.dates.signatureDate);
        const signatureStep: IStep = {
          icon: <Event />,
          label: format(signatureDate, "PP"),
          labelProps: {
            optional: t("Signature date"),
          },
          description: "",
          created: signatureDate.getTime(),
        };
        newSteps.push(signatureStep);
      }

      if (contract.dates?.endDate) {
        const endDate = new Date(contract.dates.endDate);
        const endStep: IStep = {
          icon: <Event />,
          label: format(endDate, "PP"),
          labelProps: {
            optional: t("End date"),
          },
          description: "",
          created: endDate.getTime(),
        };
        newSteps.push(endStep);
      }
    }

    if (latestEndDate) {
      const endDateStep: IStep = {
        icon: <Event />,
        label: format(latestEndDate, "PP"),
        labelProps: {
          optional: t("End date"),
        },
        description: "",
        created: latestEndDate.getTime(),
      };

      newSteps.push(endDateStep);
    }

    if (contract.noticePeriod) {
      if (!latestEndDate) {
        const noticeStep: IStep = {
          icon: <Event />,
          label: t("No end date"),
          labelProps: {
            optional: t("Notice period"),
          },
          description: "",
          created: new Date().getTime(),
        };

        newSteps.push(noticeStep);
      } else if (latestEndDate) {
        const noticeDate = contract.noticePeriod
          ? subPeriodFromDate(latestEndDate, contract.noticePeriod)
          : latestEndDate;

        const description = t("notification_period_date_text", {
          value: contract.noticePeriod?.value ?? 0,
          unit: t(contract.noticePeriod?.unit),
          beforeDate: latestEndDate
            ? t("date.dateOnly", {
                date: subPeriodFromDate(latestEndDate, contract.noticePeriod),
              })
            : t("No end date"),
        });

        const noticeStep: IStep = {
          icon: <Event />,
          label: format(noticeDate, "PP"),
          labelProps: {
            optional: t("Latest termination date"),
          },
          description: description,
          created: noticeDate.getTime(),
        };

        newSteps.push(noticeStep);
      }
    }

    // add a today step
    const today = new Date();
    const todayStep: IStep = {
      icon: <Event />,
      label: format(today, "PP"),
      labelProps: {
        optional: t("Today"),
      },
      description: "",
      created: today.getTime(),
    };

    newSteps.push(todayStep);

    // sort the steps by created date
    newSteps.sort((a, b) => a.created - b.created);

    setSteps(newSteps);
  }, [contract]);

  return (
    <Stepper activeStep={1} orientation="horizontal">
      {steps?.map((step, i) => (
        <Step key={step.label}>
          <StepLabel {...step.labelProps} StepIconComponent={ColorlibStepIcon}>
            {step.label}{" "}
            {step.description && (
              <Tooltip title={step.description} arrow placement="top">
                <Info color="info" fontSize="small" />
              </Tooltip>
            )}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};

interface IContractTimelineProps {
  contract: IContract;
  documents?: IDocument[];
  aiOutputs?: ContractExtractionResult[];
}
function ContractTimeline({ ...props }: IContractTimelineProps) {
  const { t } = useTranslation();

  const { data: activities = [], refetch } = useGetActivitiesForContractQuery(
    props.contract.id,
    { skip: !props.contract?.id }
  );

  // const activities: IActivity[] = [];
  // const refetch = () => {};

  // {
  //     id: contract??.owner,
  //   } && { skipToken: !contract??.owner }

  useEffect(() => {
    if (props.contract?.id) {
      refetch();
    }
  }, [props.contract?.id, refetch]);

  return (
    <Grid size={12}>
      {/* {props.contract?.created && props.contract?.dates && (
        <TimelineStepper contract={props.contract} />
      )} */}

      {props.contract?.lifecycle && (
        <BasicTimeline contract={props.contract} documents={props.documents} />
      )}
    </Grid>
  );
}

export default ContractTimeline;
